import React from 'react';
import PropTypes from 'prop-types';

import {Alert, Box, Button, CircularProgress, Pill, Stack, Typography} from 'views/components';
import {IosShare as ShareIcon} from 'views/icons';
import {translate} from 'views/module';

import InvitationShareDialog from './InvitationShareDialog';
import RecordingPlayer from './RecordingPlayer';
import useInvitationRecordings from './useInvitationRecordings';

import moment from 'moment';

const STRINGS = 'career.jobDetail.Invitations';

const answerLink = (recordings, position) => {
  return recordings?.familyAnswers?.find((entry) => entry.position === position)?.audio || null;
};

const Label = (props) => {
  return (
    <Typography sx={{fontWeight: 500, fontSize: '11px', lineHeight: '14px', color: '#A8A49E'}}>
      {props.children}
    </Typography>
  );
};
Label.propTypes = {
  children: PropTypes.node.isRequired,
};

const FamilyAnswer = (props) => {
  const {familyAnswer, recordings, loading, loadFailed, refresh} = props;
  const {position} = familyAnswer;

  const link = answerLink(recordings, position);
  const getLink = React.useCallback(async () => answerLink(await refresh(), position), [refresh, position]);

  // Loom gives no link when it stored no clip, or when its recordings bucket is not set.
  const hasNoClip = !familyAnswer.audioKey || (!loading && !loadFailed && !link);

  return (
    <Stack
      spacing={1}
      sx={{
        p: 1.5,
        borderRadius: '8px',
        backgroundColor: '#F9F8F6',
        border: '1px solid #E8E4DE',
      }}
    >
      <Typography sx={{fontSize: '13px', fontWeight: 500, color: '#1A1916', wordBreak: 'break-word'}}>
        {`${position}. ${familyAnswer.questionText}`}
      </Typography>
      {
        familyAnswer.audioKey && loading &&
        <CircularProgress size={20} />
      }
      {
        familyAnswer.audioKey && !loading && link &&
        <RecordingPlayer
          link={link}
          getLink={getLink}
          label={familyAnswer.questionText}
        />
      }
      {
        hasNoClip &&
        <Typography sx={{fontSize: '12px', color: '#6B6760'}}>
          {translate(`${STRINGS}.FamilyAnswers.NoClip`)}
        </Typography>
      }
      <Box>
        <Label>{translate(`${STRINGS}.FamilyAnswers.Transcript`)}</Label>
        <Typography sx={{fontSize: '13px', color: '#1A1916', whiteSpace: 'pre-wrap', wordBreak: 'break-word'}}>
          {familyAnswer.answerTranscript || translate(`${STRINGS}.FamilyAnswers.NoTranscript`)}
        </Typography>
      </Box>
      <Box>
        <Label>{translate(`${STRINGS}.FamilyAnswers.Summary`)}</Label>
        <Typography sx={{fontSize: '13px', color: '#1A1916', whiteSpace: 'pre-wrap', wordBreak: 'break-word'}}>
          {familyAnswer.answer || translate(`${STRINGS}.FamilyAnswers.NotAnswered`)}
        </Typography>
      </Box>
    </Stack>
  );
};
FamilyAnswer.propTypes = {
  familyAnswer: PropTypes.shape({
    position: PropTypes.number.isRequired,
    questionText: PropTypes.string.isRequired,
    answer: PropTypes.string,
    answerTranscript: PropTypes.string,
    audioKey: PropTypes.string,
  }).isRequired,
  recordings: PropTypes.object,
  loading: PropTypes.bool.isRequired,
  loadFailed: PropTypes.bool.isRequired,
  refresh: PropTypes.func.isRequired,
};

// The family questions of an invitation, for staff: each question with the clip of the caregiver's
// answer, the answer transcript and the agent's summary. Staff can share a completed interview with
// the family here. Sharing is final.
const InvitationFamilyAnswers = (props) => {
  const {invitation, caregiverName, onShared} = props;
  const familyAnswers = invitation.familyAnswers || [];

  const hasClips = familyAnswers.some((familyAnswer) => familyAnswer.audioKey);
  const {recordings, loading, error, refresh} = useInvitationRecordings(invitation.id, hasClips);

  const [isSharing, setIsSharing] = React.useState(false);

  // A Loom with no share route (before CQ-831) sends no sharedWithFamilyAt, so the button stays hidden.
  const canShare = invitation.status === 'COMPLETED' &&
    familyAnswers.length > 0 &&
    invitation.sharedWithFamilyAt === null;

  const onDialogShared = React.useCallback(() => {
    setIsSharing(false);
    onShared();
  }, [onShared]);

  return (
    <Stack spacing={1}>
      <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{gap: 1, flexWrap: 'wrap'}}>
        <Typography sx={{fontSize: '13px', fontWeight: 700, color: '#1A1916'}}>
          {translate(`${STRINGS}.FamilyAnswers.Title`)}
        </Typography>
        {
          invitation.sharedWithFamilyAt &&
          <Pill
            label={translate(`${STRINGS}.Share.Shared`, {
              value: moment(invitation.sharedWithFamilyAt).format(translate('timeFormat.ShortMonthDayTime')),
            })}
            variant='info'
          />
        }
        {
          canShare &&
          <Button
            size='small'
            variant='outlined'
            startIcon={<ShareIcon />}
            onClick={() => setIsSharing(true)}
            sx={{textTransform: 'none'}}
          >
            {translate(`${STRINGS}.Share.Button`)}
          </Button>
        }
      </Stack>
      {
        error &&
        <Alert severity='error'>{translate(`${STRINGS}.FamilyAnswers.LoadError`)}</Alert>
      }
      {
        familyAnswers.map((familyAnswer) => (
          <FamilyAnswer
            key={familyAnswer.id || familyAnswer.position}
            familyAnswer={familyAnswer}
            recordings={recordings}
            loading={loading}
            loadFailed={Boolean(error)}
            refresh={refresh}
          />
        ))
      }
      {
        isSharing &&
        <InvitationShareDialog
          invitationId={invitation.id}
          caregiverName={caregiverName}
          onClose={() => setIsSharing(false)}
          onShared={onDialogShared}
        />
      }
    </Stack>
  );
};
InvitationFamilyAnswers.propTypes = {
  invitation: PropTypes.shape({
    id: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    sharedWithFamilyAt: PropTypes.string,
    familyAnswers: PropTypes.array,
  }).isRequired,
  caregiverName: PropTypes.string,
  onShared: PropTypes.func.isRequired,
};

export default React.memo(InvitationFamilyAnswers);
